import { useAuth } from './use-auth';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Configuration, Customer } from 'oas';
import { applyDevIdentityHeaders } from './dev-identity-headers';
import * as React from 'react';
import { useSnackbar } from 'shared';
import { setupDatasharingMock } from 'api/datasharing/__mocks__/datasharing-mock';
import { API_NAMES } from 'api/constants';
import { v4 as uuid4 } from 'uuid';

export const applyActivityServiceRequestId = (instance: AxiosInstance): void => {
    instance.interceptors.request.use((config) => {
        config.headers = { ...(config.headers ?? {}), 'X-Request-ID': uuid4() };
        return config;
    });
};

const getApiBasePath = (apiName?: string): string | undefined => {
    if (!apiName) {
        return undefined;
    }

    const apiUrlMap: Record<string, string | undefined> = {
        [API_NAMES.DATASHARING]: process.env.REACT_APP_DATASHARING_API_URL,
        [API_NAMES.ACTIVITY_SERVICE]: process.env.REACT_APP_ACTIVITY_SERVICE_API_URL,
        [API_NAMES.PLANNING]: process.env.REACT_APP_PLANNING_API_URL,
        [API_NAMES.USER_CONFIG]: process.env.REACT_APP_USER_CONFIG_API_URL,
        [API_NAMES.ATHLETE_SERVICE]: process.env.REACT_APP_ATHLETE_SERVICE_API_URL,
    };

    return apiUrlMap[apiName];
};

export const useApiConfig = (
    apiVersion: string,
    apiName?: string,
): { apiConfig: Configuration; axiosInstance: AxiosInstance } => {
    const basePath = getApiBasePath(apiName) || process.env.REACT_APP_API_HOST + '/' + apiVersion;
    const { errorSnackbar } = useSnackbar();

    const auth = useAuth();
    const customer = useQueryClient().getQueryData<Customer>(['customer']);

    // APIs that don't need a JWT in dev: datasharing is mocked, and planning talks to a
    // locally-run service authenticated via X-Customer-* headers instead of the
    // gateway-verified JWT. Other external APIs (e.g. user config, which local dev calls
    // through the deployed dev gateway) still need one.
    const DEV_APIS_WITHOUT_JWT: string[] = [API_NAMES.DATASHARING, API_NAMES.PLANNING, API_NAMES.ATHLETE_SERVICE];
    const skipAccessToken =
        process.env.REACT_APP_ENVIRONMENT === 'dev' && !!apiName && DEV_APIS_WITHOUT_JWT.includes(apiName);

    const apiConfig = new Configuration({
        basePath,
        accessToken: apiName && !skipAccessToken ? async () => await auth.getAccessToken() : undefined,
    });

    const axiosInstance: AxiosInstance = React.useMemo(() => {
        const newAxiosInstance = axios.create();

        if (apiName === API_NAMES.ACTIVITY_SERVICE && getApiBasePath(apiName)) {
            applyActivityServiceRequestId(newAxiosInstance);
        }

        if (process.env.REACT_APP_ENVIRONMENT === 'dev') {
            applyDevIdentityHeaders(newAxiosInstance, auth.user, customer);
        }

        // setup mocks for external APIs in dev env
        if (process.env.REACT_APP_ENVIRONMENT === 'dev' && apiName) {
            const accountId = auth.user?.customer_id;
            setupDatasharingMock(newAxiosInstance, accountId);
        }

        if (auth.user) {
            newAxiosInstance.interceptors.response.use(
                (response: AxiosResponse) => response,
                async (error: AxiosError) => {
                    if (error.response) {
                        const status = error.response?.status;

                        // eslint-disable-next-line no-console
                        console.debug('interceptor status', status);

                        if (status === 401) {
                            auth.logout();
                        } else if (status >= 500 && status <= 599) {
                            const message = error.response.data.message;

                            // errors from the zendesk auth can be sporadic (due to 429's or 500's)
                            // but they are not critical, therefore, we do not want to notify the user of this
                            if (error.response.config.url?.includes('/api/v6/zendesk/auth')) {
                                return;
                            }

                            errorSnackbar(message || 'Unknown error');
                        }
                    }

                    // JWT-authenticated APIs (apiName set) reject so react-query and callers see the
                    // real failure. The legacy v6 path still resolves with the AxiosError; its many
                    // callers assume that contract and need a separate audit before it can reject too.
                    return apiName ? Promise.reject(error) : error;
                },
            );
        }

        return newAxiosInstance;
    }, [auth, errorSnackbar, apiName, customer]);

    return { apiConfig, axiosInstance };
};
